import { useLayoutEffect, useRef } from 'react';
import type { FeedbackKind } from '@/lib/celebration';
import { completionMotion, completionTiming, useEffectiveReducedMotion } from '@/lib/motion';

const paths: Record<FeedbackKind, string> = {
  payment: 'M5 12l4 4L19 6',
  contribution: 'M5 19v-5m7 5v-9m7 9V5M4 9l7-6 4 3 5-4',
  import: 'M6 3h9l4 4v5M6 3v18h6M14 3v5h5M14 17l3 3 5-6',
  milestone: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z',
};

/** Decorative: the adjacent toast text carries the full result for sighted and screen-reader users. */
export function CompletionMark({ kind }: { kind: FeedbackKind }) {
  const ref = useRef<SVGSVGElement>(null);
  const reduced = useEffectiveReducedMotion();
  useLayoutEffect(() => {
    if (reduced || document.visibilityState === 'hidden' || !ref.current?.animate) return;
    const animation = ref.current.animate(completionMotion[kind], completionTiming);
    const hide = () => {
      if (document.visibilityState === 'hidden') animation.cancel();
    };
    document.addEventListener('visibilitychange', hide);
    return () => {
      animation.cancel();
      document.removeEventListener('visibilitychange', hide);
    };
  }, [kind, reduced]);
  return (
    <svg
      ref={ref}
      data-completion-kind={kind}
      aria-hidden="true"
      focusable="false"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, transformOrigin: 'center' }}
    >
      <path d={paths[kind]} />
    </svg>
  );
}
